import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import RefreshIcon from '@mui/icons-material/Refresh'
import SyncIcon from '@mui/icons-material/Sync'
import { promotionSyncAdminService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import {
  formatCount,
  formatDate,
  formatDuration,
  promotionSyncCronScheduleMessage,
  statusColor,
  statusLabel,
  triggerLabel,
} from './promotionSyncAdminFormat'

const POLL_INTERVAL_MS = 5000
const AWAITING_POLL_MS = 1500
const AWAITING_TIMEOUT_MS = 45000
const STUCK_AFTER_MS = 8 * 60 * 1000

const headCellSx = { fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }

const Stat = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {formatCount(value)}
    </Typography>
  </Box>
)

const PromotionSyncAdminPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.STOCK_SYNC,
    'write',
  )
  const [latest, setLatest] = useState(null)
  const [schedule, setSchedule] = useState(null)
  const [history, setHistory] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [awaitingNewRun, setAwaitingNewRun] = useState(false)
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [unavailableIds, setUnavailableIds] = useState({})
  const [error, setError] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })
  const [nowTick, setNowTick] = useState(Date.now())
  const [failedOpen, setFailedOpen] = useState(false)
  const [failedRunId, setFailedRunId] = useState(null)
  const [failedRows, setFailedRows] = useState([])
  const [failedTotal, setFailedTotal] = useState(0)
  const [failedPage, setFailedPage] = useState(0)
  const [failedLimit, setFailedLimit] = useState(20)
  const [failedQuery, setFailedQuery] = useState('')
  const [failedLoading, setFailedLoading] = useState(false)
  const [failedError, setFailedError] = useState(null)
  const awaitingBaselineRunIdRef = useRef(null)
  const awaitingNewRunSinceRef = useRef(null)
  const latestRequestSeqRef = useRef(0)

  const loadLatest = useCallback(async () => {
    const seq = ++latestRequestSeqRef.current
    const data = await promotionSyncAdminService.getLatestRun()
    // Drop stale responses so an older poll cannot overwrite a newer stop/start state.
    if (seq !== latestRequestSeqRef.current) return
    setLatest(data || null)
  }, [])

  const loadSchedule = useCallback(async () => {
    const data = await promotionSyncAdminService.getSchedule()
    setSchedule(data || null)
  }, [])

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const data = await promotionSyncAdminService.listRuns({
        page: page + 1,
        limit: rowsPerPage,
      })
      setHistory(data?.items || [])
      setTotal(data?.pagination?.total || 0)
    } finally {
      setHistoryLoading(false)
    }
  }, [page, rowsPerPage])

  const refresh = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      await Promise.all([loadLatest(), loadHistory(), loadSchedule()])
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to load promotion sync runs'
      setError(typeof message === 'string' ? message : 'Failed to load promotion sync runs')
    } finally {
      setLoading(false)
    }
  }, [loadLatest, loadHistory, loadSchedule])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const latestRunning = latest?.status === 'running'
  const shouldPoll = latestRunning || awaitingNewRun

  useEffect(() => {
    if (!shouldPoll) return undefined
    const intervalMs = awaitingNewRun && !latestRunning ? AWAITING_POLL_MS : POLL_INTERVAL_MS
    const timer = setInterval(() => {
      setNowTick(Date.now())
      void loadLatest().catch(() => {})
      void loadHistory().catch(() => {})
    }, intervalMs)
    return () => clearInterval(timer)
  }, [shouldPoll, awaitingNewRun, latestRunning, loadLatest, loadHistory])

  useEffect(() => {
    if (!awaitingNewRun) return undefined

    const clearAwaiting = () => {
      setAwaitingNewRun(false)
      awaitingNewRunSinceRef.current = null
      awaitingBaselineRunIdRef.current = null
    }

    const baselineId = awaitingBaselineRunIdRef.current
    const latestId = latest?.id ?? null
    const status = latest?.status
    const isNewRun = latestId != null && latestId !== baselineId
    const isTerminal =
      status === 'failed' || status === 'completed' || status === 'cancelled'

    if (status === 'running' || (isNewRun && isTerminal)) {
      clearAwaiting()
      void loadHistory()
      return undefined
    }

    const startedAt = awaitingNewRunSinceRef.current
    if (startedAt != null && nowTick - startedAt > AWAITING_TIMEOUT_MS) {
      clearAwaiting()
    }
    return undefined
  }, [awaitingNewRun, latest?.status, latest?.id, loadHistory, nowTick])

  const loadFailed = useCallback(async () => {
    if (!failedOpen || !failedRunId) return
    setFailedLoading(true)
    setFailedError(null)
    try {
      const data = await promotionSyncAdminService.listFailedLocations(failedRunId, {
        page: failedPage + 1,
        limit: failedLimit,
        q: failedQuery || undefined,
      })
      setFailedRows(data?.items || [])
      setFailedTotal(data?.pagination?.total || 0)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to load failed locations'
      setFailedError(typeof message === 'string' ? message : 'Failed to load failed locations')
      setFailedRows([])
      setFailedTotal(0)
    } finally {
      setFailedLoading(false)
    }
  }, [failedOpen, failedRunId, failedPage, failedLimit, failedQuery])

  useEffect(() => {
    void loadFailed()
  }, [loadFailed])

  const beginAwaitingNewRun = () => {
    awaitingBaselineRunIdRef.current = latest?.id ?? null
    awaitingNewRunSinceRef.current = Date.now()
    setAwaitingNewRun(true)
  }

  const handleRun = async () => {
    try {
      setStarting(true)
      setError(null)
      beginAwaitingNewRun()
      const data = await promotionSyncAdminService.run()
      setSnackbar({
        open: true,
        severity: 'success',
        message: data?.message || 'Promotion sync started',
      })
      setPage(0)
      await Promise.all([loadLatest(), loadHistory()])
    } catch (err) {
      const status = err.response?.status
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to start promotion sync'
      if (status === 409) {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: typeof message === 'string' ? message : 'Promotion sync already running',
        })
        beginAwaitingNewRun()
        await Promise.all([loadLatest(), loadHistory()])
      } else {
        setAwaitingNewRun(false)
        awaitingNewRunSinceRef.current = null
        awaitingBaselineRunIdRef.current = null
        setError(typeof message === 'string' ? message : 'Failed to start promotion sync')
      }
    } finally {
      setStarting(false)
    }
  }

  const handleStop = async () => {
    if (!latest?.id) return
    try {
      setStopping(true)
      setError(null)
      const stopped = await promotionSyncAdminService.stopRun(latest.id)
      // Apply backend-confirmed view immediately; do not infer stopped from acceptance alone.
      if (stopped && typeof stopped === 'object') {
        latestRequestSeqRef.current += 1
        setLatest(stopped)
      }
      const confirmedCancelled = stopped?.status === 'cancelled'
      setSnackbar({
        open: true,
        severity: 'success',
        message: confirmedCancelled
          ? 'Orphaned sync marked cancelled. You can start a new sync.'
          : 'Stop requested. Sync will finish the current batch and then cancel.',
      })
      await Promise.all([loadLatest(), loadHistory()])
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to stop promotion sync'
      setError(typeof message === 'string' ? message : 'Failed to stop promotion sync')
    } finally {
      setStopping(false)
    }
  }

  const handleDownloadApi = async (runId) => {
    const unavailableKey = `api-${runId}`
    try {
      setDownloadingKey(unavailableKey)
      const data = await promotionSyncAdminService.getApiData(runId)
      if (!data?.download_url) {
        throw new Error('No download URL')
      }
      window.open(data.download_url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      const status = err.response?.status
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to download API data'
      if (status === 404) {
        setUnavailableIds((prev) => ({ ...prev, [unavailableKey]: true }))
        setSnackbar({
          open: true,
          severity: 'warning',
          message: typeof message === 'string' ? message : 'Artifact is no longer available',
        })
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: typeof message === 'string' ? message : 'Failed to download API data',
        })
      }
    } finally {
      setDownloadingKey(null)
    }
  }

  const handleDownloadFailed = async (runId) => {
    try {
      setDownloadingKey(`failed-${runId}`)
      await promotionSyncAdminService.downloadFailedLocationsCsv(runId)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to download failed locations'
      setSnackbar({
        open: true,
        severity: 'error',
        message: typeof message === 'string' ? message : 'Failed to download failed locations',
      })
    } finally {
      setDownloadingKey(null)
    }
  }

  const openFailed = (runId) => {
    setFailedRunId(runId)
    setFailedPage(0)
    setFailedQuery('')
    setFailedOpen(true)
  }

  const canDownloadApi = (run) => {
    if (!run?.id) return false
    if (unavailableIds[`api-${run.id}`]) return false
    return Boolean(run.api_data_csv_s3_key)
  }

  const locationsTotal = Number(latest?.locations_total) || 0
  const locationsProcessed = Number(latest?.locations_processed) || 0
  const progressPct =
    locationsTotal > 0
      ? Math.min(100, Math.round((locationsProcessed / locationsTotal) * 100))
      : latestRunning
        ? 0
        : latest
          ? 100
          : 0
  const heartbeatMs = latest?.last_progress_at
    ? new Date(latest.last_progress_at).getTime()
    : latest?.started_at
      ? new Date(latest.started_at).getTime()
      : null
  const possiblyStuck =
    Boolean(latest?.possibly_stuck) ||
    (latestRunning &&
      heartbeatMs != null &&
      !Number.isNaN(heartbeatMs) &&
      nowTick - heartbeatMs > STUCK_AFTER_MS)
  const runDisabled = loading || starting || awaitingNewRun || latestRunning
  const cronScheduleMessage = promotionSyncCronScheduleMessage(schedule)

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <LocalOfferIcon color="primary" fontSize="large" />
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Promotion sync
        </Typography>
        <Button
          variant="outlined"
          startIcon={
            loading || historyLoading ? <CircularProgress size={18} /> : <RefreshIcon />
          }
          onClick={refresh}
          disabled={loading || historyLoading}
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Pull POS promotions from Shwapno manage-api into pos_store_promotions. Only one
        promotion sync can run at a time. History is kept for 30 days. API and failed-location
        files expire after 7 days.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Cron
          </Typography>
          {loading && !schedule ? (
            <CircularProgress size={22} />
          ) : (
            <Stack spacing={0.5}>
              <Alert severity="info" sx={{ mb: 1 }}>
                {cronScheduleMessage}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                This is the configured automatic schedule. It is separate from a manual sync
                below and does not mean a run is in progress or that the last run succeeded.
              </Typography>
              <Typography variant="body2">
                Schedule value: <strong>{schedule?.schedule || 'every 270 minutes'}</strong>
                {schedule?.interval_minutes != null
                  ? ` (${schedule.interval_minutes} minutes)`
                  : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {schedule?.description ||
                  'Every 4 hours and 30 minutes (270 minutes) as a recurring interval from the host scheduler.'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Command: {schedule?.command || 'python cron.py promotions'}
                {schedule?.log_file ? ` (host log file: ${schedule.log_file})` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The job is enabled by the host scheduler. This page shows the latest execution
                and can start another run when none is in progress. Worker lifecycle events are
                written to the host process log; run status and error summaries appear below.
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Manual sync
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {canWrite
                  ? 'Starts the same job as the cron. A second start is skipped while one is already running.'
                  : 'You can view sync runs. Starting or stopping a sync requires write permission.'}
              </Typography>
            </Box>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={
                  starting || awaitingNewRun ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )
                }
                onClick={handleRun}
                disabled={runDisabled}
              >
                {starting || awaitingNewRun
                  ? 'Starting…'
                  : latestRunning
                    ? 'Sync running…'
                    : 'Run promotion sync'}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        {(starting || awaitingNewRun || latestRunning) && <LinearProgress />}
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Latest run
              </Typography>
              {latest && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" variant="outlined" label={statusLabel(latest)} color={statusColor(latest)} />
                  <Chip size="small" variant="outlined" label={triggerLabel(latest)} />
                </Stack>
              )}
            </Box>
            {latest && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {canWrite && latestRunning && (
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={stopping || (Boolean(latest.cancel_requested_at) && !possiblyStuck)}
                    onClick={handleStop}
                  >
                    {stopping
                      ? 'Stopping…'
                      : latest.cancel_requested_at && possiblyStuck
                        ? 'Force stop'
                        : latest.cancel_requested_at
                          ? 'Stopping…'
                          : 'Stop sync'}
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={
                    downloadingKey === `api-${latest.id}` ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={!canDownloadApi(latest) || downloadingKey === `api-${latest.id}`}
                  onClick={() => handleDownloadApi(latest.id)}
                >
                  API data
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={downloadingKey === `failed-${latest.id}`}
                  onClick={() => handleDownloadFailed(latest.id)}
                >
                  Failed locations
                </Button>
              </Stack>
            )}
          </Stack>

          {loading && !latest ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !latest ? (
            <Typography variant="body2" color="text.secondary">
              No sync runs recorded yet.
            </Typography>
          ) : (
            <Box>
              {possiblyStuck && !latest.cancel_requested_at && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Possibly stuck — no progress for over 8 minutes. Use Stop sync to close the
                  run so a new sync can start.
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Triggered by</Typography>
                  <Typography>{latest.triggered_by_email || triggerLabel(latest)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Started</Typography>
                  <Typography>{formatDate(latest.started_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Finished</Typography>
                  <Typography>
                    {latest.finished_at
                      ? formatDate(latest.finished_at)
                      : latestRunning
                        ? 'In progress'
                        : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography>
                    {formatDuration(latest.started_at, latest.finished_at, nowTick)}
                  </Typography>
                </Grid>
              </Grid>
              {locationsTotal > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Progress: {formatCount(locationsProcessed)} / {formatCount(locationsTotal)} locations
                  {latestRunning ? ` (${progressPct}%)` : ''}
                  {latest.date_from ? ` · ${latest.date_from} to ${latest.date_to || '—'}` : ''}
                </Typography>
              )}
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Stat label="Processed" value={latest.locations_processed} />
                <Stat label="Failed locations" value={latest.locations_failed} />
                <Stat label="Inserted" value={latest.rows_inserted} />
                <Stat label="Updated" value={latest.rows_updated} />
                <Stat label="Skipped" value={latest.rows_skipped} />
              </Stack>
              {(Number(latest.locations_failed) || 0) > 0 && (
                <Button sx={{ mt: 1 }} onClick={() => openFailed(latest.id)}>
                  View failed locations
                </Button>
              )}
              {latest.error_summary && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {latest.error_summary}
                </Alert>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                Last progress {formatDate(latest.last_progress_at)}
                {latestRunning ? ` · refreshed ${new Date(nowTick).toLocaleTimeString()}` : ''}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            History (30 days)
          </Typography>
        </Box>
        {historyLoading && history.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 200, p: 3 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 3 }}>
            No promotion sync runs in the last 30 days.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx}>Trigger</TableCell>
                  <TableCell sx={headCellSx}>Triggered by</TableCell>
                  <TableCell sx={headCellSx}>Started</TableCell>
                  <TableCell sx={headCellSx}>Finished</TableCell>
                  <TableCell sx={headCellSx} align="right">Processed</TableCell>
                  <TableCell sx={headCellSx} align="right">Failed</TableCell>
                  <TableCell sx={headCellSx} align="right">Inserted</TableCell>
                  <TableCell sx={headCellSx} align="right">Updated</TableCell>
                  <TableCell sx={headCellSx}>Downloads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      <Chip size="small" label={statusLabel(run)} color={statusColor(run)} variant="outlined" />
                    </TableCell>
                    <TableCell>{triggerLabel(run)}</TableCell>
                    <TableCell>{run.triggered_by_email || '—'}</TableCell>
                    <TableCell>{formatDate(run.started_at)}</TableCell>
                    <TableCell>{formatDate(run.finished_at)}</TableCell>
                    <TableCell align="right">{formatCount(run.locations_processed)}</TableCell>
                    <TableCell align="right">
                      {(Number(run.locations_failed) || 0) > 0 ? (
                        <Button size="small" onClick={() => openFailed(run.id)}>
                          {formatCount(run.locations_failed)}
                        </Button>
                      ) : (
                        formatCount(run.locations_failed)
                      )}
                    </TableCell>
                    <TableCell align="right">{formatCount(run.rows_inserted)}</TableCell>
                    <TableCell align="right">{formatCount(run.rows_updated)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          disabled={!canDownloadApi(run)}
                          onClick={() => handleDownloadApi(run.id)}
                        >
                          API data
                        </Button>
                        <Button size="small" onClick={() => handleDownloadFailed(run.id)}>
                          Failed
                        </Button>
                      </Stack>
                      {run.error_summary && (
                        <Typography variant="caption" color="error" display="block">
                          {run.error_summary}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>

      <Dialog open={failedOpen} onClose={() => setFailedOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Failed locations
          <IconButton sx={{ ml: 'auto' }} onClick={() => setFailedOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="Search"
              value={failedQuery}
              onChange={(event) => {
                setFailedQuery(event.target.value)
                setFailedPage(0)
              }}
              fullWidth
            />
            <Button
              variant="outlined"
              disabled={!failedRunId}
              onClick={() => failedRunId && handleDownloadFailed(failedRunId)}
            >
              Download CSV
            </Button>
          </Stack>
          {failedError && <Alert severity="error" sx={{ mb: 2 }}>{failedError}</Alert>}
          {failedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : failedRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No failed locations for this run.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Failed at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedRows.map((row) => (
                  <TableRow key={`${row.location_code}-${row.failed_at}`}>
                    <TableCell>{row.location_code || '—'}</TableCell>
                    <TableCell>{row.location_name || '—'}</TableCell>
                    <TableCell>{row.reason || '—'}</TableCell>
                    <TableCell>{formatDate(row.failed_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <TablePagination
            component="div"
            count={failedTotal}
            page={failedPage}
            onPageChange={(_event, next) => setFailedPage(next)}
            rowsPerPage={failedLimit}
            onRowsPerPageChange={(event) => {
              setFailedLimit(parseInt(event.target.value, 10))
              setFailedPage(0)
            }}
            rowsPerPageOptions={[20, 50, 100]}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default PromotionSyncAdminPage
